'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { colors, formatNum, formatUSD } from './SharedUI'; 

const baseStyles = {
  card: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)' },
  flexBetween: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  select: { backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.textMain, padding: '6px 12px', borderRadius: '6px', outline: 'none', cursor: 'pointer', fontSize: '13px' },
  btn: { backgroundColor: colors.bg, color: colors.textMain, padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px' }
};

export default function SmartMoneyFlowVelocityV2({ symbol, smartMoneyRef, liveDataRef }) {
  const [sysState, setSysState] = useState('IDLE'); 
  const [macroContext, setMacroContext] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  
  const [selectedTimeframe, setSelectedTimeframe] = useState('30m'); 
  const [isAuto, setIsAuto] = useState(false);
  const [isAutoSnap, setIsAutoSnap] = useState(false);
  
  const [history, setHistory] = useState([]);
  const [lastUpdate, setLastScan] = useState(null);

  // [CẬP NHẬT TRÍ NHỚ]: Dùng để lưu Absolute USD của Radar thay vì Flow
  const tickMemoryRef = useRef(null); 
  const lastSnapRef = useRef({ status: null, time: 0 });

  const timeframes = [
    { id: '30m', label: '30 Phút', parent: '1h' },
    { id: '1h', label: '1 Giờ', parent: '24h' },
    { id: '24h', label: '24 Giờ', parent: '7D' },
    { id: '7D', label: '7 Ngày', parent: null } 
  ];

  const fetchStatsApi = async (tf) => {
    if (!tf) return null;
    const res = await fetch(`https://www.binance.com/bapi/futures/v1/public/future/smart-money/signal/details/stats?symbol=${symbol.toUpperCase()}&timeRange=${tf}`);
    const json = await res.json();
    return json.success ? json.data : null;
  };

  const runSetupSystem = async () => {
    if (!symbol) return;
    
    const liveSM = smartMoneyRef?.current;
    const livePrice = parseFloat(liveDataRef?.current?.last || 0);

    if (!liveSM || !livePrice) {
      alert("Đang tải dữ liệu gốc từ Radar. Vui lòng chờ 1-2 giây rồi thử lại!");
      return;
    }

    setSysState('SCANNING_MACRO');
    setData(null);
    tickMemoryRef.current = null; 

    const data1h = await fetchStatsApi('1h');

    if (liveSM && data1h) {
      const l24 = (liveSM.longTradersQty || 0) * livePrice;
      const s24 = (liveSM.shortTradersQty || 0) * livePrice;
      const dom24 = l24 > s24 ? { side: 'LONG', val: l24 - s24 } : { side: 'SHORT', val: s24 - l24 };
      
      const l1 = data1h.longPositions || 0;
      const s1 = data1h.shortPositions || 0;
      const dom1 = l1 > s1 ? { side: 'LONG', val: l1 - s1 } : { side: 'SHORT', val: s1 - l1 };
      
      const txt24 = dom24.side === 'LONG' ? `🟢 24H: Long đè (+${formatUSD(dom24.val)})` : `🔴 24H: Short đè (+${formatUSD(dom24.val)})`;
      const txt1 = dom1.side === 'LONG' ? `🟢 1H: Long rướn (+${formatUSD(dom1.val)})` : `🔴 1H: Short ép (+${formatUSD(dom1.val)})`;
      
      setMacroContext(`${txt24} | ${txt1}`);
    }

    setSelectedTimeframe('30m');
    setIsAuto(true);
    setIsAutoSnap(true);
    setSysState('READY');
    
    fetchLiveVelocity('30m');
  };

  const handleStop = () => setIsAuto(false);
  const handleRestart = () => setIsAuto(true);
  const handleReset = () => {
    setSysState('IDLE');
    setIsAuto(false);
    setIsAutoSnap(false);
    setData(null);
    setMacroContext(null);
    tickMemoryRef.current = null;
    lastSnapRef.current = { status: null, time: 0 };
  };

  const fetchLiveVelocity = useCallback(async (overrideTf = null) => {
    if (!symbol) return;
    
    const liveSM = smartMoneyRef?.current;
    const livePrice = parseFloat(liveDataRef?.current?.last || 0);

    if (!liveSM || !livePrice) return;

    setLoading(true);
    const tf = overrideTf || selectedTimeframe;
    const tfConfig = timeframes.find(t => t.id === tf);

    try {
      const [targetData, parentData] = await Promise.all([
        fetchStatsApi(tf),
        fetchStatsApi(tfConfig.parent)
      ]);

      if (targetData && liveSM) {
        processFlowData(targetData, parentData, liveSM, livePrice, tf);
      }
    } catch (error) {
      console.error("Lỗi Fetch Live:", error);
    } finally {
      setLoading(false);
    }
  }, [symbol, selectedTimeframe, smartMoneyRef, liveDataRef]);

  useEffect(() => {
    let intervalId;
    if (isAuto && sysState === 'READY') {
      intervalId = setInterval(() => { fetchLiveVelocity(); }, 15000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [isAuto, sysState, fetchLiveVelocity]);

  useEffect(() => {
    handleReset();
    setHistory([]);
  }, [symbol]);

  const processFlowData = (targetData, parentData, baseData, currentPrice, tfId) => {
    const tfLabel = timeframes.find(t => t.id === tfId).label;
    const tfHours = tfId === '30m' ? 0.5 : (tfId === '1h' ? 1 : (tfId === '24h' ? 24 : 168));
    const baseHours = 24; 

    // Helper: Lấy Dữ liệu Trạng Thái (Absolute USD) từ Radar
    const getBaseUSD = (prefix, isWhale, sourceData = baseData) => {
      if (!sourceData) return 0;
      if (sourceData[`${prefix}Positions`] !== undefined) {
        return isWhale ? (sourceData[`${prefix}WhalePositions`] || 0) : (sourceData[`${prefix}Positions`] || 0);
      }
      return isWhale ? (sourceData[`${prefix}WhalesQty`] * currentPrice || 0) : (sourceData[`${prefix}TradersQty`] * currentPrice || 0);
    };

    const extractMetrics = (targetUSD, baseUSD, targetTraders) => {
      const currentUSD = targetUSD; 
      const currentTraders = targetTraders;
      
      const avgUSDPerHourBase = baseUSD / baseHours;
      const avgUSDPerHourTarget = targetUSD / tfHours;
      const volSpeedRatio = avgUSDPerHourBase === 0 ? 1 : avgUSDPerHourTarget / avgUSDPerHourBase;

      const avgPerTrader = currentTraders > 0 ? currentUSD / currentTraders : 0;
      return { currentUSD, currentTraders, volSpeedRatio, avgPerTrader };
    };

    const baseLongWhaleUSD = getBaseUSD('long', true);
    const baseLongTotalUSD = getBaseUSD('long', false);
    const wl = extractMetrics(targetData.longWhalePositions, baseLongWhaleUSD, targetData.longWhales);
    const rl = extractMetrics(targetData.longPositions - targetData.longWhalePositions, baseLongTotalUSD - baseLongWhaleUSD, targetData.longTraders - targetData.longWhales);

    const baseShortWhaleUSD = getBaseUSD('short', true);
    const baseShortTotalUSD = getBaseUSD('short', false);
    const ws = extractMetrics(targetData.shortWhalePositions, baseShortWhaleUSD, targetData.shortWhales);
    const rs = extractMetrics(targetData.shortPositions - targetData.shortWhalePositions, baseShortTotalUSD - baseShortWhaleUSD, targetData.shortTraders - targetData.shortWhales);

    const totals = { longUSD: targetData.longPositions, shortUSD: targetData.shortPositions, longTraders: targetData.longTraders, shortTraders: targetData.shortTraders };

    // ==========================================
    // [FIX LỖI TICK MEMORY]: Dùng Absolute USD
    // ==========================================
    let tickAlert = "";
    if (tickMemoryRef.current && isAuto) {
      const prev = tickMemoryRef.current;
      // Trừ trực tiếp tổng tài sản đang ôm (State)
      const deltaWL_15s = baseLongWhaleUSD - prev.absLongWhale;
      const deltaWS_15s = baseShortWhaleUSD - prev.absShortWhale;
      
      // Nếu Cá Mập đột ngột nạp thêm tiền mạnh vào Vị thế Tổng (> 10,000$ trong 15s để tránh nhiễu)
      if (deltaWL_15s > 10000) tickAlert = `⚡ Cá Mập LONG vừa nhồi +${formatUSD(deltaWL_15s)} trong 15s qua!`;
      else if (deltaWS_15s > 10000) tickAlert = `⚡ Cá Mập SHORT vừa nhồi +${formatUSD(deltaWS_15s)} trong 15s qua!`;
    }
    // Ghi nhớ Trạng thái Tổng vào mảng cho nhịp sau
    tickMemoryRef.current = { absLongWhale: baseLongWhaleUSD, absShortWhale: baseShortWhaleUSD };

    // ==========================================
    // BẮT HIỆN TƯỢNG QUAY XE (30m > 1h)
    // ==========================================
    let isLongReversal = false;
    let isShortReversal = false;
    if (parentData) {
      const parentWlUSD = getBaseUSD('long', true, parentData);
      const parentWsUSD = getBaseUSD('short', true, parentData);
      if (wl.currentUSD > parentWlUSD && parentWlUSD > 0) isLongReversal = true;
      if (ws.currentUSD > parentWsUSD && parentWsUSD > 0) isShortReversal = true;
    }

    // AI NHẬN DIỆN KỊCH BẢN (MACRO)
    let scenario = { status: "BÌNH THƯỜNG", title: "Dòng tiền đi ngang, không có đột biến rẽ nhánh.", color: colors.textMain, action: "Quan sát thêm." };

    if (isLongReversal) {
      scenario = { status: "🚀 CÚ QUAY XE BẮT ĐÁY (LONG REVERSAL)", color: colors.green, title: `Khối lượng ${tfLabel} > Khung cha! Phe Bán đã kiệt sức. Cá mập Long vừa vớt đáy toàn bộ lực bán trước đó.`, action: "Tín hiệu đảo chiều CỰC MẠNH. Tìm điểm LONG ngay!" };
    } else if (isShortReversal) {
      scenario = { status: "🩸 CÚ QUAY XE ÚP SỌT (SHORT REVERSAL)", color: colors.red, title: `Khối lượng ${tfLabel} > Khung cha! Phe Mua bị chặn đứng. Cá mập Short vừa xả lượng hàng khủng khiếp đè nát phe Long.`, action: "Tín hiệu đảo chiều CỰC MẠNH. Bỏ chạy hoặc SHORT ngay!" };
    } else if (rl.volSpeedRatio > 1.5 && wl.volSpeedRatio < 1.0 && ws.volSpeedRatio > 1.2) {
      scenario = { status: "🚨 PHÂN PHỐI ĐỈNH (BULL TRAP)", color: colors.red, title: `Nhỏ lẻ Long đang FOMO nạp ${formatUSD(rl.currentUSD)}, Cá mập Long đã dừng. Cá mập Short đang chủ động nã đạn!`, action: "TUYỆT ĐỐI KHÔNG LONG! Canh nhịp hồi để SHORT." };
    } else if (rs.volSpeedRatio > 1.5 && ws.volSpeedRatio <= 1.0 && wl.volSpeedRatio > 1.0) {
      scenario = { status: "⚠️ DẤU HIỆU RŨ HÀNG (BEAR TRAP)", color: colors.warn, title: `Nhỏ lẻ hoảng loạn Short ${formatUSD(rs.currentUSD)}, Cá mập Short đứng ngoài. Cá mập Long âm thầm gom hàng.`, action: "Cẩn thận bẫy giảm giá! Chuẩn bị nến xanh chọc trời để diệt Short." };
    } else if (ws.volSpeedRatio > 2.0 && ws.currentUSD > wl.currentUSD * 1.5) {
      scenario = { status: "🔴 SHORT CHỦ ĐỘNG", color: colors.red, title: `Cá mập phe Short ồ ạt tấn công. Áp lực đè giá cực lớn.`, action: "Phe Gấu cầm trịch. Thuận nước đẩy thuyền SHORT." };
    } else if (wl.volSpeedRatio > 2.0 && wl.currentUSD > ws.currentUSD * 1.5) {
      scenario = { status: "🟢 LONG CHỦ ĐỘNG", color: colors.green, title: `Cá mập phe Long gom hàng quyết liệt. Lực mua áp đảo.`, action: "Sóng tăng uy tín. Canh Pullback để LONG." };
    }

    // Ghép cái Cảnh báo 15s vào Đầu bản tin
    if (tickAlert !== "") {
      scenario.title = `${tickAlert} ${scenario.title}`;
      if (scenario.status === "BÌNH THƯỜNG") {
        scenario.status = "⚡ CÓ DÒNG TIỀN ĐỘT BIẾN (MICRO-SPIKE)";
        scenario.color = colors.warn;
      }
    }

    const newData = { wl, rl, ws, rs, totals, scenario, tfLabel: tfLabel };
    setData(newData);
    setLastScan(new Date().toLocaleTimeString('vi-VN'));

    if (isAutoSnap && scenario.status !== "BÌNH THƯỜNG") {
      const now = Date.now();
      const lastSnap = lastSnapRef.current;
      if (scenario.status !== lastSnap.status || tickAlert !== "" || (now - lastSnap.time > 300000)) {
        saveSnapshot(newData, '🤖 AUTO-CAPTURED');
        lastSnapRef.current = { status: scenario.status, time: now };
      }
    }
  };

  const saveSnapshot = (snapData = data, type = '📸 MANUAL') => {
    if (!snapData) return;
    const newSnap = { id: Date.now(), time: new Date().toLocaleTimeString('vi-VN'), type, ...snapData };
    setHistory(prev => [newSnap, ...prev]);
  };

  const removeSnapshot = (id) => {
    setHistory(prev => prev.filter(item => item.id !== id));
  };

  const UIBox = ({ label, volSpeedRatio, currentUSD, currentTraders, avgPerTrader }) => {
    let speedText = "Bình thường"; let speedColor = colors.textSub; let arrow = "➖";
    if (volSpeedRatio > 1.5) { speedText = "Tăng tốc mạnh"; speedColor = colors.green; arrow = "🚀"; }
    else if (volSpeedRatio > 1.1) { speedText = "Tăng tốc nhẹ"; speedColor = colors.green; arrow = "📈"; }
    else if (volSpeedRatio < 0.5) { speedText = "Dừng lại"; speedColor = colors.red; arrow = "🛑"; }
    else if (volSpeedRatio < 0.9) { speedText = "Chậm lại"; speedColor = colors.red; arrow = "🔻"; }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ color: colors.textSub, fontSize: '12px', fontWeight: 'bold' }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px' }}>{arrow}</span>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ color: speedColor, fontWeight: 'bold', fontSize: '13px' }}>{speedText} <span style={{fontSize:'11px', fontWeight:'normal'}}>(x{volSpeedRatio.toFixed(1)})</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}><span style={{ color: colors.textSub }}>Khối Lượng Ròng:</span><strong style={{ color: colors.textMain }}>{formatUSD(currentUSD)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}><span style={{ color: colors.textSub }}>Số Traders Mới:</span><strong style={{ color: colors.textMain }}>{formatNum(currentTraders)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: `1px dashed rgba(255,255,255,0.1)`, paddingTop: '4px' }}><span style={{ color: colors.textSub }}>Quy mô TB/Lệnh:</span><strong style={{ color: colors.warn }}>{formatUSD(avgPerTrader)}</strong></div>
          </div>
        </div>
      </div>
    );
  };

  const renderDataMatrix = (snapData) => {
    const lUSD = snapData.totals.longUSD;
    const sUSD = snapData.totals.shortUSD;
    const cLongUSD = lUSD > sUSD ? colors.green : (lUSD < sUSD ? colors.red : colors.textMain);
    const cShortUSD = sUSD > lUSD ? colors.green : (sUSD < lUSD ? colors.red : colors.textMain);

    const lTraders = snapData.totals.longTraders;
    const sTraders = snapData.totals.shortTraders;
    const cLongTraders = lTraders > sTraders ? colors.green : (lTraders < sTraders ? colors.red : colors.textMain);
    const cShortTraders = sTraders > lTraders ? colors.green : (sTraders < lTraders ? colors.red : colors.textMain);

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        <div style={{ border: `1px solid ${colors.green}`, borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ backgroundColor: colors.greenBg, padding: '10px', textAlign: 'center', fontWeight: 'bold', color: colors.green, fontSize: '14px' }}>🟢 PHE LONG (BÒ)</div>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', padding: '10px 16px', borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}><span style={{ color: colors.textSub }}>Tổng Tiền Đổ Vào:</span><strong style={{ color: cLongUSD }}>{formatUSD(lUSD)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}><span style={{ color: colors.textSub }}>Tổng Số Traders:</span><strong style={{ color: cLongTraders }}>{formatNum(lTraders)}</strong></div>
          </div>
          <div>
            <UIBox label="1. CÁ MẬP (WHALES)" volSpeedRatio={snapData.wl.volSpeedRatio} currentUSD={snapData.wl.currentUSD} currentTraders={snapData.wl.currentTraders} avgPerTrader={snapData.wl.avgPerTrader} />
            <UIBox label="2. NHỎ LẺ (RETAIL)" volSpeedRatio={snapData.rl.volSpeedRatio} currentUSD={snapData.rl.currentUSD} currentTraders={snapData.rl.currentTraders} avgPerTrader={snapData.rl.avgPerTrader} />
          </div>
        </div>

        <div style={{ border: `1px solid ${colors.red}`, borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ backgroundColor: colors.redBg, padding: '10px', textAlign: 'center', fontWeight: 'bold', color: colors.red, fontSize: '14px' }}>🔴 PHE SHORT (GẤU)</div>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', padding: '10px 16px', borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}><span style={{ color: colors.textSub }}>Tổng Tiền Đổ Vào:</span><strong style={{ color: cShortUSD }}>{formatUSD(sUSD)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}><span style={{ color: colors.textSub }}>Tổng Số Traders:</span><strong style={{ color: cShortTraders }}>{formatNum(sTraders)}</strong></div>
          </div>
          <div>
            <UIBox label="1. CÁ MẬP (WHALES)" volSpeedRatio={snapData.ws.volSpeedRatio} currentUSD={snapData.ws.currentUSD} currentTraders={snapData.ws.currentTraders} avgPerTrader={snapData.ws.avgPerTrader} />
            <UIBox label="2. NHỎ LẺ (RETAIL)" volSpeedRatio={snapData.rs.volSpeedRatio} currentUSD={snapData.rs.currentUSD} currentTraders={snapData.rs.currentTraders} avgPerTrader={snapData.rs.avgPerTrader} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
      
      <div style={{ ...baseStyles.card, border: `1px solid ${sysState === 'READY' ? colors.green : colors.border}` }}>
        
        {/* HEADER VÀ CONTROL PANEL */}
        <div style={{ ...baseStyles.flexBetween, borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <strong style={{ color: colors.textMain, fontSize: '16px' }}>🌊 HỆ THỐNG RADAR SĂN SMART MONEY (V2)</strong>
          </div>
          
          {sysState === 'IDLE' && (
            <button onClick={runSetupSystem} style={{ ...baseStyles.btn, backgroundColor: colors.greenBg, color: colors.green, border: `1px solid ${colors.green}` }}>
              🚀 KHỞI ĐỘNG HỆ THỐNG
            </button>
          )}

          {sysState === 'SCANNING_MACRO' && (
            <div style={{ color: colors.warn, fontWeight: 'bold', fontSize: '13px' }}>⏳ Đang thiết lập Vĩ mô...</div>
          )}

          {sysState === 'READY' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {isAuto ? (
                <button onClick={handleStop} style={{ ...baseStyles.btn, backgroundColor: 'rgba(239, 68, 68, 0.1)', color: colors.red, border: `1px solid ${colors.red}` }}>🛑 Tạm Dừng</button>
              ) : (
                <button onClick={handleRestart} style={{ ...baseStyles.btn, backgroundColor: colors.greenBg, color: colors.green, border: `1px solid ${colors.green}` }}>▶️ Tiếp Tục</button>
              )}
              <button onClick={handleReset} style={{ ...baseStyles.btn, backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.textMain }}>🔄 Reset</button>
              <div style={{ width: '1px', height: '20px', backgroundColor: colors.border, margin: '0 8px' }}></div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: isAutoSnap ? '#3b82f6' : colors.textSub }}>
                <input type="checkbox" checked={isAutoSnap} onChange={(e) => setIsAutoSnap(e.target.checked)} style={{ width: '16px', height: '16px' }} />
                {isAutoSnap ? '📸 Auto-Snap' : '⚪ Tắt Snap'}
              </label>
              <div style={{ width: '1px', height: '20px', backgroundColor: colors.border, margin: '0 8px' }}></div>
              <select value={selectedTimeframe} onChange={(e) => { setSelectedTimeframe(e.target.value); if (isAuto) { fetchLiveVelocity(e.target.value); } }} style={baseStyles.select}>
                {timeframes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <button onClick={() => fetchLiveVelocity(selectedTimeframe)} disabled={loading} style={{ ...baseStyles.btn, border: `1px solid ${colors.warn}`, color: colors.warn, backgroundColor: 'transparent' }}>
                {loading ? '⏳...' : '⚡ Quét Nhanh'}
              </button>
            </div>
          )}
        </div>

        {sysState === 'READY' && (
          <div style={{ backgroundColor: isAuto ? 'rgba(34, 197, 94, 0.05)' : 'rgba(245, 158, 11, 0.05)', border: `1px solid ${isAuto ? colors.green : colors.warn}50`, borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ height: '10px', width: '10px', backgroundColor: isAuto ? colors.green : colors.warn, borderRadius: '50%', display: 'inline-block' }}></span>
              <strong style={{ color: isAuto ? colors.green : colors.warn, fontSize: '14px' }}>TRẠNG THÁI: {isAuto ? 'READY (ĐANG ĐI SĂN)' : 'PAUSED (ĐÃ TẠM DỪNG)'}</strong>
            </div>
            <div style={{ fontSize: '13px', color: colors.textSub, paddingLeft: '18px', lineHeight: '1.6' }}>
              <strong>Macro Context:</strong> {macroContext || 'Đang đồng bộ...'} <br/>
              <strong>Cấu hình chạy:</strong> Soi luồng [ {selectedTimeframe} ] | Auto Quét: {isAuto ? 'BẬT' : 'TẮT'} | Auto-Snap: {isAutoSnap ? 'BẬT' : 'TẮT'}
            </div>
          </div>
        )}

        {/* KẾT QUẢ LIVE */}
        {data && sysState === 'READY' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ ...baseStyles.flexBetween, flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ fontSize: '13px', color: colors.textSub, fontStyle: 'italic' }}>
                *Khối lượng Nạp/Rút ròng trong <strong>[ {data.tfLabel.toUpperCase()} QUA ]</strong>. (Gia tốc so với Live Radar 24H).
              </div>
              <button onClick={() => saveSnapshot(data, '📸 MANUAL')} style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid #3b82f6', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                📸 CHỤP NHẬT KÝ
              </button>
            </div>

            <div style={{ backgroundColor: `${data.scenario.color}15`, color: data.scenario.color, padding: '12px', borderRadius: '8px', fontWeight: 'bold', fontSize: '15px', textAlign: 'center', border: `1px dashed ${data.scenario.color}50` }}>
              LIVE: {data.scenario.status}
            </div>

            {renderDataMatrix(data)}

            <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '8px', borderLeft: `4px solid ${data.scenario.color}` }}>
              <div style={{ fontSize: '13px', color: colors.textSub, marginBottom: '8px' }}><strong style={{color: colors.textMain}}>🧠 AI KẾT LUẬN:</strong> {data.scenario.title}</div>
              <div style={{ fontSize: '13px' }}><strong style={{color: colors.textMain}}>👉 KHUYẾN NGHỊ:</strong> <span style={{ color: data.scenario.color, fontWeight: 'bold' }}>{data.scenario.action}</span></div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '11px', color: colors.textSub }}>Cập nhật lần cuối: {lastUpdate}</div>
          </div>
        )}
      </div>

      {/* LỊCH SỬ SNAPSHOT */}
      {history.length > 0 && (
        <div style={{ ...baseStyles.card, borderColor: colors.border }}>
          <div style={{ fontWeight: 'bold', color: colors.textMain, fontSize: '15px', marginBottom: '16px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px' }}>
            📋 NHẬT KÝ SNAPSHOT (LƯU LẠI KHI CÓ BIẾN)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxHeight: '800px', overflowY: 'auto', paddingRight: '4px' }}>
            {history.map(item => (
              <div key={item.id} style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: `1px solid ${item.scenario.color}50`, borderRadius: '8px', padding: '16px', borderLeft: `4px solid ${item.scenario.color}` }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: `1px dotted ${colors.border}`, paddingBottom: '8px' }}>
                  <div style={{ fontSize: '13px', color: colors.textSub }}>
                    🕒 <strong>{item.time}</strong> | 🔍 KHUNG SOI: <strong>[{item.tfLabel.toUpperCase()}]</strong> | <span style={{ color: item.type.includes('AUTO') ? colors.warn : '#3b82f6', fontWeight: 'bold' }}>{item.type}</span>
                  </div>
                  <button onClick={() => removeSnapshot(item.id)} style={{ background: 'transparent', border: 'none', color: colors.red, cursor: 'pointer', fontSize: '16px' }}>✖</button>
                </div>

                <div style={{ fontWeight: 'bold', color: item.scenario.color, fontSize: '15px', marginBottom: '16px', textAlign: 'center', padding: '8px', backgroundColor: `${item.scenario.color}15`, borderRadius: '6px' }}>
                  🔥 KẾT LUẬN: {item.scenario.status}
                </div>

                {renderDataMatrix(item)}

                <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '8px', borderLeft: `4px solid ${item.scenario.color}`, marginTop: '16px' }}>
                  <div style={{ fontSize: '13px', color: colors.textSub, marginBottom: '8px' }}><strong style={{color: colors.textMain}}>🧠 LÝ DO LƯU (AI KẾT LUẬN):</strong><br/>{item.scenario.title}</div>
                  <div style={{ fontSize: '13px' }}><strong style={{color: colors.textMain}}>👉 HÀNH ĐỘNG:</strong> <span style={{ color: item.scenario.color, fontWeight: 'bold' }}>{item.scenario.action}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
